<template>
  <b-container v-if="(SKURangeFlagEnabled  && !textButtonEnabled) && categoryReady" class="p-0">
    <b-row no-gutters>
      <b-col :cols="onlyTextInput ? 12: 11">
        <SelectActivityRenderer
            ref="skuRenderer"
            :custom-mode="true"
            :field-config="selectFormFieldConfig"
            :masterFormModel="masterFormModel"
            :mode="mode"
            :parent-form="parentForm"
            :validated-field="validatedField"
            @custom-mode-event="showNumbers"
        >
        </SelectActivityRenderer>
      </b-col>
      <b-col v-if="!onlyTextInput" cols="1">
        <b-button :disabled="readOnly" class="input-group-text solid"
                  style="height: 38px; margin-top: 21px;" @click="changeTypeofFieldToEditableText">
          <font-awesome-icon icon="exchange"/>
        </b-button>
      </b-col>
    </b-row>
  </b-container>

  <ReachInput
      v-else-if="categoryReady || SKUOnlyText"
      :editor-mode="editorMode"
      :field-config="fieldConfig"
      :formField="validatedField"
      :help-message="fieldConfig.labelOnly ? '' :fieldConfig.htmlHelpText"
      :help-title="fieldConfig.labelOnly ? '' : fieldConfig.helpWindowTitle"
      :hint="fieldConfig.labelOnly ? '' :fieldConfig.shortHelpText"
      :initialFocus="fieldConfig.autofocus"
      :label="fieldConfig.labelOnly ? '\u00A0' : fieldConfig.fieldLabel"
      :read-only="readOnly || !validatedField || validatedField.readOnly"
  >
    <template v-if="textButtonEnabled || SKUForNaoko" slot="right-slot">
      <b-button v-if="textButtonEnabled" class="input-group-text solid" style="height: 38px"
                @click="changeTypeofFieldToEditableText">
        <font-awesome-icon icon="refresh"/>
      </b-button>

      <b-button v-if="SKUForNaoko && formIsProject" :disabled="readOnlyButton || lockedDuringProcessRpcRequest"
                class="input-group-text solid"
                style="height: 38px"
                @click="generateSKU">
        <font-awesome-icon icon="refresh"/>
      </b-button>
    </template>

  </ReachInput>
</template>

<script>

import InputBase from "@/components/inputs/InputBase";
import ReachInput from "@/components/inputs/ReachInput";
import FormValidatedField from "@/components/forms/FormValidatedField";
import TextFormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/TextFormFieldConfig";
import SelectActivityRenderer from "@/components/forms/renderers/SelectActivityRenderer.vue";
import SelectFormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/SelectFormFieldConfig";
import FormsActions from "@/components/forms/FormsActions";
import CodeAuxiliaryUnitNumber from "@/server/com/silkplm/model/plm/dictionaries/CodeAuxiliaryUnitNumber";
import NumericFilterSpec from "@/server/com/silkplm/engine/forms/schema/definition/filters/NumericFilterSpec";
import Category from "@/server/com/silkplm/model/plm/Category";
import {
  checkIfEnabledByOrganizationFormsSettingsForForm, findFieldValueFromCompanyCompanyConfigurationFormField
} from "@/components/forms/customizations/plm/EntityEditFormService";
import Rpc from "@/Rpc";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";
import FeatureFlag from "@/server/com/silkplm/rpc/FeatureFlag";
import ColorVersion from "@/server/com/silkplm/model/plm/ColorVersion";
import dialogManager from "@/utils/DialogManager";
import UpgradeOptions from "@/server/com/silkplm/model/plm/UpgradeOptions";


export default {
  name: "SkuNumberRenderer",
  components: {SelectActivityRenderer, InputBase, ReachInput},
  async created() {
    this.selectFormFieldConfig = new SelectFormFieldConfig();
    this.selectFormFieldConfig.foreignClass = CodeAuxiliaryUnitNumber.getRemoteClass();
    this.selectFormFieldConfig.fieldLabel = "SKU Number";
    this.selectFormFieldConfig.fieldName = "SKU Number";
    this.masterFormModel = this.parentForm;
  },

  async mounted() {
    this.SKURangeFlagEnabled = await Rpc.plm.services.FeatureFlagService.selectFlagIs("skuNumberPlugin", "SKU for Lavard");
    this.SKUOnlyText = await Rpc.plm.services.FeatureFlagService.selectFlagIs("skuNumberPlugin", "Text");
    this.SKUForNaoko = await Rpc.plm.services.FeatureFlagService.selectFlagIs("skuNumberPlugin", "SKU for NAOKO");
    if (this.SKUOnlyText || this.SKUForNaoko) {
      this.onlyTextInput = true;
    } else if (SessionManager.hasFlag(FeatureFlag.SKU_FIELD_ACTIVE_IN_COLOR_VERSIONS)) {
      this.onlyTextInput = this.form.formClassLong !== ColorVersion.getRemoteClass();
    } else {
      this.onlyTextInput = this.form.formClassLong !== ProductInSeason.getRemoteClass();
    }
    setTimeout(async () => {
      await this.afterMounted();
    });
  },

  props: {
    selectedRows: {default: null},
    openFromGrid: {default: false},
    openFromMultiSelectGrid: {default: false},
    validatedField: {
      type: FormValidatedField, required: true
    },
    fieldConfig: {
      type: TextFormFieldConfig, required: true
    },
    parentForm: {
      default: null
    },
    mode: {
      default: null
    }
  },

  methods: {
    async changeTypeofFieldToEditableText() {
      if (!this.textButtonEnabled) {
        if (await DialogManager.yesNo($tr('dangerousOperation!', 'Dangerous operation!'),
            $tr('skuNumberRenderer.attention', '<b>Attention! Entering a number that is already defined in the ERP system will link the products " +\n' +
                '            "between the systems and update the data based on the data entered in the SilkPLM system.</b>'),
            true, $tr('submit', 'Submit'), $tr('skuNumber.cancel', 'Cancel'))) {
          this.textButtonEnabled = !this.textButtonEnabled;
        }
      } else {
        this.textButtonEnabled = !this.textButtonEnabled;
      }
      setTimeout(async () => {
        if (!this.textButtonEnabled) {
          await this.$refs.skuRenderer.setReadOnly(false);
          this.$refs.skuRenderer.setEditorMode(true);
        }
      });
    },

    async generateSKU() {
      if (!this.openFromMultiSelectGrid) {
        await this.generateSKUNumberForNAOKO(this.validatedField.validatedForm.originalSource);
      }
      let selectedEntities = [];
      if (this.selectedRows?.materialize) {
        await this.selectedRows.materialize();
        for (let item of this.selectedRows) {
          selectedEntities.push(item);
        }
      }
      let projectsWithNotEmptySkuNumber = [];
      selectedEntities.forEach(entity => {
        if (entity.SKUNumber?.length) {
          projectsWithNotEmptySkuNumber.push(entity.numberOfProduct);
        }
      });

      if (projectsWithNotEmptySkuNumber.length) {
        let projectsText = "";
        projectsWithNotEmptySkuNumber.forEach(numberOfProduct => {
          projectsText += numberOfProduct + ", ";
        });
        if (projectsText.lastIndexOf(", ") !== -1) {
          let index = projectsText.lastIndexOf(",");
          projectsText = projectsText.substring(0, index) + projectsText.substring(index + 1);
        }
        let message = $tr("skuNumberRenderer.notEmptyWarning", "Projects with numbers: #{1} " +
            "has already not empty values - you can't assign SKU Number.", projectsText);
        await dialogManager.info($tr("skuNumberRenderer.skuWarningTitle", "Can't assign SKU Number"), message);
        return;
      }

      this.bulkErrorMessage = "";
      for (const entity of selectedEntities) {
        await this.generateSKUNumberForNAOKO(entity);
      }

      if (!this.bulkErrorMessage?.length) {
        let res = new Map();
        for (const el of selectedEntities) {
          res[el.id] = el.SKUNumber;
        }
        await Rpc.plm.services.ProductInSeasonService.bulkAssignSkuNumber(res);
      } else {
        await dialogManager.info($tr("skuNumberRenderer.skuWarningTitle", "Can't assign SKU Number"), this.bulkErrorMessage);
      }
    },

    async generateSKUNumberForNAOKO(entity) {
      try {
        this.lockedDuringProcessRpcRequest = true;
        let SKU = await Rpc.plm.services.ProductService.computeSkuForProductInSeasonForNaokoPathAfterClickButton(
            entity.id,
            entity.category,
            entity.formNumberId,
            entity.seasonId,
            entity.countryOfOrigin,
        ).lock();
        if (SKU?.length) {
          if (this.openFromGrid) {
            if (!this.openFromMultiSelectGrid) {
              entity.SKUNumber = SKU;
              this.parentForm.originalSource.shouldUpgradeSKUSequence = true;
              this.validatedField.validatedForm.validatedFieldByName("shouldUpgradeSKUSequence").value = true;
              entity.shouldUpgradeSKUSequence = true;
              await entity.save();
            }
          }
        }
        if (!SKU) {
          let warning = await Rpc.plm.services.ProductInSeasonService.computeSKUWarning(
              entity.numberOfProduct,
              entity.category,
              entity.formNumberId,
              entity.seasonId,
              entity.countryOfOrigin
          ).lock();
          if (warning && warning.length) {
            if (!this.openFromMultiSelectGrid) {
              await dialogManager.info($tr("skuNumberRenderer.skuWarningTitle", "Can't assign SKU Number"), warning);
            } else {
              this.bulkErrorMessage += warning + " <br>";
            }
          }
        } else {
          if (!this.openFromMultiSelectGrid) {
            this.validatedField.value = SKU;
            entity = SKU;
            this.validatedField.validatedForm.validatedFieldByName("shouldUpgradeSKUSequence").value = true;
          }
        }
      } finally {
        this.lockedDuringProcessRpcRequest = false;
      }
    },

    async afterMounted() {
      if (this.SKURangeFlagEnabled) {
        this.validatedField.foreignValueLabel = this.validatedField.value;
      }
      let formConfig = this.validatedField.validatedForm.formConfig;
      let alternativeFieldLabel = findFieldValueFromCompanyCompanyConfigurationFormField(formConfig, this.fieldConfig,
          "alternativeFieldLabel");
      if (alternativeFieldLabel?.length) {
        this.selectFormFieldConfig.fieldLabel = alternativeFieldLabel;
        this.selectFormFieldConfig.fieldName = alternativeFieldLabel;
      }
      await this.assignCategory();
      this.handleChangeReadOnly();
    }
    ,

    async assignCategory() {
      let originalSource = this.validatedField.validatedForm.originalSource;
      if (!originalSource) {
        return;
      }
      if (this.fieldConfig.joined) {
        return;
      }
      let formClass = this.validatedField.validatedForm.formConfig.formClassLong;
      let categoryId = null;
      if (formClass === ProductInSeason.getRemoteClass()) {
        categoryId = originalSource.category;
      } else {
        let productInSeason = await originalSource.getProductInSeasonIdEntity();
        categoryId = productInSeason.category;
      }
      if (categoryId) {
        let category = await Rpc.Forms.getExtendedEntity(Category.getRemoteClass(), categoryId);
        if (category.CodeAuxiliaryUnitRangeFrom?.length) {
          this.rangeFrom = Number(category.CodeAuxiliaryUnitRangeFrom);
        } else {
          this.rangeFrom = null;
        }
        if (category.CodeAuxiliaryUnitRangeTo?.length) {
          this.rangeTo = Number(category.CodeAuxiliaryUnitRangeTo);
        } else {
          this.rangeTo = null;
        }
        if (this.rangeFrom && this.rangeTo) {
          let filter = new NumericFilterSpec().withMin(this.rangeFrom).withMax(this.rangeTo);
          let filterSpecs = {
            fieldName: "SKUNumeric",
            fieldLabel: "SKU",
            filterSpec:
            filter
          };
          this.rangeFilters = {customFilter: [filterSpecs]}; // inkluzywne!
        } else {
          this.rangeFilters = {customExcludedEligibleFieldNames: ["SKUNumeric"]};
        }
      } else {
        this.rangeFilters = {customExcludedEligibleFieldNames: ["SKUNumeric"]};
      }
      this.categoryReady = true;
      setTimeout(() => {
        if (this.mode === "NEW") {
          this.setReadOnly(false);
        }
      })
    }
    ,
    async setReadOnly(readOnly) {
      if (this.openFromGrid) {
        this.SKUOnlyText = await Rpc.plm.services.FeatureFlagService.selectFlagIs("skuNumberPlugin", "Text");
        this.SKUForNaoko = await Rpc.plm.services.FeatureFlagService.selectFlagIs("skuNumberPlugin", "SKU for NAOKO");
      }
      if (this.SKUOnlyText || this.SKUForNaoko) {
        this.readOnly = readOnly;
      }
      if (this.onlyTextInput) {
        readOnly = true;
      }
      if (this.SKURangeFlagEnabled && !this.textButtonEnabled) {
        this.readOnly = this.fieldConfig.readOnly || readOnly;
        this.$refs.skuRenderer?.setReadOnly(this.readOnly);
      }
      this.handleChangeReadOnly();
    }
    ,

    setEditorMode(val) {
      if (this.SKURangeFlagEnabled || this.SKUOnlyText || this.SKUForNaoko) {
        this.editorMode = val;
      }
    }
    ,

    async showNumbers() {
      let results = await Rpc.plm.services.DictionaryService.findExcludedCodeAuxiliaryUnitNumbers();
      let excludedIds = [];
      results.forEach(el => {
        excludedIds.push(el.id);
      });
      let props = this.rangeFilters ? {filter: this.rangeFilters, excludedIds} : {excludedIds};
      let result = await (await FormsActions.showSelectList(CodeAuxiliaryUnitNumber, props, excludedIds)).getResult();
      if (!result) {
        this.validatedField.value = "";
        this.validatedField.foreignValueLabel = "";
        return;
      }
      this.validatedField.value = result.number;
      this.validatedField.foreignValueLabel = result.number;
    }
    ,

    handleChangeReadOnly() {
      if (this.SKUForNaoko) {
        if (this.readOnly) {
          this.readOnlyButton = true;
          return;
        }
        let length = 0;
        if (this.validatedField?.value) {
          length = this.validatedField.value.length;
        }
        this.readOnlyButton = !!length;
      }
    }
  }
  ,

  watch: {
    "validatedField.validatedForm.originalSource":

        async function (val) {
          if (this.form.formClassLong === ColorVersion.getRemoteClass() && !this.categoryReady) {
            await this.assignCategory();
          }
        }

    ,
    "validatedField.validatedForm.originalSource.category":

        async function (val) {
          await this.assignCategory();
        }

    ,
    "validatedField.value":

        function (val) {
          this.handleChangeReadOnly();
        }
  }
  ,

  data: function () {
    return {
      formIsProject: this.validatedField.validatedForm.formConfig.formClassLong === ProductInSeason.getRemoteClass(),
      lockedDuringProcessRpcRequest: false,
      readOnlyButton: true,
      SKUOnlyText: false,
      SKUForNaoko: false,
      readOnly: true,
      editorMode: false,
      form: this.validatedField.validatedForm.formConfig,
      SKURangeFlagEnabled: false,
      selectFormFieldConfig: null,
      masterFormModel: null,
      categoryReady: false,
      rangeFilters: null,
      rangeFrom: null,
      rangeTo: null,
      textButtonEnabled: false,
      customExcludedEligibleFieldNames: [],
      onlyTextInput: false,
    }
  }
}

</script>

<style scoped>

</style>